import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { LINE_ITEM_DISCOUNT, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Renders promotional discount line item in the order breakdown / receipt.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {Money} [props.discountMoney] - Optional full discount money object override
 * @param {string} [props.discountCode] - The code used (e.g. RR20)
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element|null}
 */
const LineItemDiscountMaybe = props => {
  const { lineItems, discountMoney, discountCode, intl } = props;

  const discountLineItem = lineItems.find(
    item => item.code === LINE_ITEM_DISCOUNT && !item.reversal
  );

  if (!discountLineItem && !discountMoney) {
    return null;
  }

  // Format absolute value with explicit minus sign to avoid duplicate '-'
  // If discountMoney override is provided (e.g. for multi-installment full discount), prioritize it.
  const totalAmount = discountMoney
    ? discountMoney.amount
    : discountLineItem.lineTotal?.amount ??
      (discountLineItem.unitPrice?.amount
        ? discountLineItem.unitPrice.amount * (discountLineItem.quantity || -1)
        : 0);
  const absAmount = Math.abs(totalAmount);
  const currency =
    discountMoney?.currency ||
    discountLineItem?.lineTotal?.currency ||
    discountLineItem?.unitPrice?.currency ||
    'USD';
  const formattedAbs = formatMoney(intl, new Money(absAmount, currency));
  const codeToDisplay = discountCode || 'RR20';

  return (
    <div className={css.discountLineItem}>
      <span className={css.discountItemLabel}>
        <FormattedMessage id="OrderBreakdown.discountLabel" defaultMessage="Discount" />
        <span className={css.discountBadge}>{codeToDisplay}</span>
      </span>
      <span className={css.discountItemValue}>-{formattedAbs}</span>
    </div>
  );
};

export default LineItemDiscountMaybe;
